import { useCallback, useState } from 'react';
import { __ } from '@web-stories-wp/i18n';
import {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
  Icons,
  Tooltip,
  useSnackbar,
} from '@web-stories-wp/design-system';
import { StudioButton } from './studioButton';
import { useStory } from '@web-stories-wp/story-editor';
import { PAGE_WIDTH } from '@web-stories-wp/units';
import getStoryMarkup from '@web-stories-wp/story-editor/src/output/utils/getStoryMarkup';
import isDefaultPage from '@web-stories-wp/story-editor/src/utils/isDefaultPage';
import SaveTemplateDialog from '@web-stories-wp/story-editor/src/components/library/panes/pageTemplates/saveTemplateDialog';
import { useConfig } from '@web-stories-wp/story-editor';
import { useCmsApi } from '@hooks/api';
import { useTemplateSaveAssetTypes } from '@src/hooks/studioContext';

export function SavePageAsTemplateButton({ disabled }) {
  const { showSnackbar } = useSnackbar();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { currentPage, story } = useStory(({ state }) => ({
    currentPage: state.currentPage,
    story: state.story,
  }));

  const { metadata } = useConfig();
  const { studioTemplateApi } = useCmsApi();
  const saveAssetTypes = useTemplateSaveAssetTypes();

  const buttonLabel = __('Save current page as template', 'web-stories');
  const isPageEmpty = !currentPage || isDefaultPage(currentPage);
  const disabledTooltip = isPageEmpty
    ? __(
        'Add content to this page before saving it as a template.',
        'web-stories'
      )
    : '';
  const isDisabled =
    disabled || isPageEmpty || isSaving || saveAssetTypes.length === 0;

  const handleSaveTemplate = useCallback(
    async ({ title, folderId }) => {
      setIsSaving(true);

      try {
        const pageData = {
          ...currentPage,
          canvasWidth: PAGE_WIDTH,
        };
        const storyMarkup = await getStoryMarkup(
          story,
          [currentPage],
          metadata
        );

        await studioTemplateApi.save({
          jsonDefinition: JSON.stringify(pageData),
          ampHtml: storyMarkup,
          title,
          folderId,
          assetTypes: saveAssetTypes,
        });

        showSnackbar({
          message: __('Page saved as template.', 'web-stories'),
          dismissable: true,
        });

        setIsDialogOpen(false);
      } catch (error) {
        console.error('Failed to save template:', error);
        showSnackbar({
          message: __(
            'Failed to save template. Please try again.',
            'web-stories'
          ),
          dismissable: true,
        });
      } finally {
        setIsSaving(false);
      }
    },
    [
      currentPage,
      metadata,
      saveAssetTypes,
      showSnackbar,
      story,
      studioTemplateApi,
    ]
  );

  return (
    <>
      <SaveTemplateDialog
        initialTitle={currentPage?.title || ''}
        isOpen={isDialogOpen}
        isSaving={isSaving}
        onClose={() => !isSaving && setIsDialogOpen(false)}
        onSave={handleSaveTemplate}
      />
      <Tooltip title={disabledTooltip || buttonLabel} hasTail>
        <StudioButton
          variant={BUTTON_VARIANTS.SQUARE}
          type={BUTTON_TYPES.SECONDARY}
          size={BUTTON_SIZES.SMALL}
          onClick={() => setIsDialogOpen(true)}
          aria-label={
            disabledTooltip ? `${buttonLabel}. ${disabledTooltip}` : buttonLabel
          }
          disabled={isDisabled}
        >
          <Icons.Plus />
        </StudioButton>
      </Tooltip>
    </>
  );
}
